import {
  ACCOUNTING_METHOD_TYPES,
  STRIPE_FREQUENCY_TYPES,
  TAX_YEAR_END_TYPES,
} from "constants/bookkeeping";
import { emptyApi } from "./emptyApi";
import { BackendError } from "types/utils/error";

export type CategoryData = {
  section: string;
  section_category_identifiers: number[];
  uuid: string;
  title: string;
  category_name: string;
  coa_description: string;
  category?: {
    uuid: string;
    name: string;
    identifier: number;
  };
};

export type BooksLockDateResponse = {
  accounting_lock_date: string;
  gusto_access: boolean;
  deferred_revenue_enabled: boolean; //Auto-Defer & Recognize Revenue
  deferred_expenses_enabled: boolean; //Auto-Defer & Recognize Expense
  accounts_receivable_enabled: boolean; // Auto-Manage Accounts Receivable
  accounts_payable_enabled: boolean; // Auto-Manage Accounts Payable
  accounting_method: ACCOUNTING_METHOD_TYPES;
  accounts_receivable_from_date: string | null;
  accounts_payable_from_date: string | null;
  defer_revenue_from_date: string | null;
  defer_expenses_from_date: string | null;
  stripe_aggregate_frequency: STRIPE_FREQUENCY_TYPES;
  tax_year_end: TAX_YEAR_END_TYPES;
  category_data: CategoryData[];
  send_completed_financials_email: boolean;
  completed_financials_email_recipients: string[];
};

export const booksLockDateApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getBookkeepingSettings: build.query<
      BooksLockDateResponse,
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bookkeeping_settings/`,
        };
      },
      providesTags: ["BOOKS_LOCK_DATE"],
      transformErrorResponse: (response) => response as BackendError,
    }),

    updateBookkeepingSettings: build.mutation<
      BooksLockDateResponse,
      {
        entityId: string;
        accounting_lock_date?: string | null;
        gusto_access?: boolean;
        defer_revenue?: boolean; // Auto-Defer & Recognize Revenue
        defer_expenses?: boolean; // Auto-Defer & Recognize Expense
        accounts_receivable_enabled?: boolean; // Auto-Manage Accounts Receivable
        accounts_payable_enabled?: boolean; // Auto-Manage Accounts Payable
        accounts_receivable_from_date?: string | null;
        accounts_payable_from_date?: string | null;
        accounting_method?: ACCOUNTING_METHOD_TYPES;
        defer_revenue_from_date?: string | null;
        defer_expenses_from_date?: string | null;
        stripe_aggregate_frequency?: STRIPE_FREQUENCY_TYPES;
        tax_year_end?: TAX_YEAR_END_TYPES;
        transaction_category_id?: string;
        category_name?: string;
        send_completed_financials_email?: boolean;
        completed_financials_email_recipients?: string[];
      }
    >({
      query: ({
        entityId,
        accounting_lock_date,
        gusto_access,
        defer_revenue,
        defer_expenses,
        accounts_receivable_enabled,
        accounts_payable_enabled,
        accounting_method,
        accounts_receivable_from_date,
        accounts_payable_from_date,
        defer_revenue_from_date,
        defer_expenses_from_date,
        stripe_aggregate_frequency,
        tax_year_end,
        transaction_category_id,
        category_name,
        send_completed_financials_email,
        completed_financials_email_recipients,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bookkeeping_settings/`,
          method: "PUT",
          body: {
            accounting_lock_date,
            gusto_access,
            defer_revenue,
            defer_expenses,
            accounts_receivable_enabled,
            accounts_payable_enabled,
            accounting_method,
            accounts_receivable_from_date,
            accounts_payable_from_date,
            defer_revenue_from_date,
            defer_expenses_from_date,
            stripe_aggregate_frequency,
            tax_year_end,
            transaction_category_id,
            category_name,
            send_completed_financials_email,
            completed_financials_email_recipients,
          },
        };
      },
      invalidatesTags: ["BOOKS_LOCK_DATE", "JOURNAL_ENTRIES", "REPORTS"],
      transformErrorResponse: (response) => response as BackendError,
    }),

    getBookkeepingLogs: build.query<
      {
        logs: {
          field_name: string;
          new_value: string;
          old_value: string;
          changed_by: {
            uuid: string;
            email: string;
            name: string;
          };
          created_at: string;
        }[];
      },
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bookkeeping_settings_log/`,
        };
      },
      providesTags: ["BOOKS_LOCK_DATE"],
      transformErrorResponse: (response) => response as BackendError,
    }),
  }),
});

export const {
  useGetBookkeepingLogsQuery,
  useGetBookkeepingSettingsQuery,
  useUpdateBookkeepingSettingsMutation,
} = booksLockDateApi;
